import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { Login } from '../auth/Login';
import { useUserProfile } from '../context/UserProfileContext';
import { supabase } from '../lib/supabaseClient';

type LoginPageProps = {
  session: Session | null;
  loading: boolean;
};

const LoginPage = ({ session, loading }: LoginPageProps) => {
  const { profile, loading: profileLoading } = useUserProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const pendingOverride = query.get('status') === 'pending';
  const isPending = pendingOverride || profile?.role === 'pending';
  /* eslint-disable @typescript-eslint/no-floating-promises */
  const handlePendingSignOut = () =>
    supabase.auth
      .signOut()
      .catch(() => null)
      .then(() => {
        navigate('/login', { replace: true });
      });
  /* eslint-enable @typescript-eslint/no-floating-promises */

  if (loading || profileLoading) {
    return <p className="muted">Booting Shanework…</p>;
  }

  if (session && !isPending) {
    return <Navigate to="/dashboard" replace />;
  }

  if (session && isPending) {
    return (
      <div className="page">
        <main className="sections">
          <section className="section login-section" id="login">
            <div className="section-heading">
              <h1>Waiting on approval</h1>
              <p>Your account is pending review. An admin will promote you to User or Admin before you can enter the dashboard.</p>
              <p className="muted">We’ll email you once your status changes. You can sign out below and check back later.</p>
            </div>
            <div className="login-actions">
              <button
                type="button"
                className="cta cta-secondary"
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  handlePendingSignOut();
                }}
              >
                <span className="material-symbols-rounded">logout</span>
                Sign out
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="page">
      <main className="sections">
        <section className="section login-section" id="login">
          <div className="section-heading">
            <h1>Sign in to Shanework</h1>
            <p>Send yourself a code, step into the dashboard, and keep building.</p>
            <div className="login-meta">
              <span>· Email-only codes, no passwords to remember.</span>
              <span>· Sessions scoped to your project so teams stay focused.</span>
              <span>· Step in now, step out later without friction.</span>
            </div>
            {isPending && !session ? <p className="pending-banner">Your account is pending admin approval.</p> : null}
          </div>
          <Login />
        </section>
      </main>
    </div>
  );
};

export default LoginPage;

